/**
 * RTWO Cryptography & Identity Engine — Trust Record Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type {
  IdentityId,
  RTWOTrustRecord,
  TrustLevel,
  TrustRecordId,
  TrustSource,
} from '../domain-models';
import type { ITrustRecordService } from '../service-interfaces';
import type { CryptoRepositories } from '../repositories/crypto-repositories';
import type { IdentityBackendRegistry } from '../registry/provider-registries';
import { CryptoEventTypes } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

let trustCounter = 0;

function generateTrustRecordId(): string {
  trustCounter += 1;
  return `trust-${trustCounter}`;
}

export class TrustRecordService extends BaseCryptoService implements ITrustRecordService {
  readonly serviceId = CRYPTO_SERVICE_IDS.TRUST_RECORD;
  readonly serviceName = 'Trust Record Service';
  readonly description = 'Manages trust records for identities via Identity Backend.';
  readonly layer = 'R7' as const;
  readonly integrations = ['System Registry', 'R7 Enterprise Services'];
  readonly requiresKeyProvider = false;
  readonly requiresIdentityBackend = true;

  constructor(
    context: CryptoServiceContext,
    private readonly repositories: CryptoRepositories,
    private readonly identityBackendRegistry: IdentityBackendRegistry
  ) {
    super(context);
  }

  createTrustRecord(
    identityId: IdentityId,
    trustLevel: TrustLevel,
    source: TrustSource
  ): RTWOTrustRecord {
    const record: RTWOTrustRecord = {
      trustRecordId: generateTrustRecordId(),
      identityId,
      trustLevel,
      source,
      status: 'not_configured',
      createdAt: null,
    };

    this.repositories.trustRecords.save(record);
    this.context.eventBus.emit(CryptoEventTypes.TRUST_RECORD_CREATED, record.trustRecordId, {
      identityId,
      trustLevel,
      source,
    });

    return record;
  }

  getTrustRecord(trustRecordId: TrustRecordId): RTWOTrustRecord | null {
    return this.repositories.trustRecords.findById(trustRecordId);
  }

  getTrustRecordsByIdentity(identityId: IdentityId): RTWOTrustRecord[] {
    if (!this.identityBackendRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.trustRecords.findByIdentity(identityId);
  }

  updateTrustLevel(trustRecordId: TrustRecordId, newLevel: TrustLevel): RTWOTrustRecord | null {
    if (!this.identityBackendRegistry.isConfigured()) {
      return null;
    }

    const existing = this.repositories.trustRecords.findById(trustRecordId);
    if (!existing) {
      return null;
    }

    this.context.eventBus.emit(CryptoEventTypes.TRUST_RECORD_UPDATED, trustRecordId, {
      newLevel,
    });

    return null;
  }
}
